/**
 * Social platforms an organizer can link to: display order, labels, brand
 * marks, and how to recognise one from a URL. Shared by the /org/<slug>
 * social buttons and the link badges in tournament descriptions
 * (lib/rich-text.ts), so both show the same icons and names.
 *
 * Brand marks are inline paths sized to a 24-box, drawn in currentColor —
 * lucide ships no brand icons.
 */

/** Display order. Discord leads: it's where players actually coordinate. */
export const SOCIAL_PLATFORMS = [
  'discord',
  'twitch',
  'youtube',
  'x',
  'instagram',
  'facebook',
  'website',
] as const

export type SocialPlatform = (typeof SOCIAL_PLATFORMS)[number]

export const SOCIAL_LABEL: Record<SocialPlatform, string> = {
  twitch: 'Twitch',
  youtube: 'YouTube',
  x: 'X',
  instagram: 'Instagram',
  discord: 'Discord',
  facebook: 'Facebook',
  website: 'Website',
}

export const SOCIAL_ICON_PATH: Record<SocialPlatform, string> = {
  twitch:
    'M4 2 2.5 5.5v13H7V22h2.5l3-3.5H17l4.5-4.5V2H4Zm15.5 11L17 15.5h-4.5l-3 3v-3H6V4h13.5v9ZM15 7.5v5h-2v-5h2Zm-5 0v5H8v-5h2Z',
  youtube:
    'M23 12s0-3.9-.5-5.8a3 3 0 0 0-2.1-2.1C18.5 3.5 12 3.5 12 3.5s-6.5 0-8.4.6A3 3 0 0 0 1.5 6.2C1 8.1 1 12 1 12s0 3.9.5 5.8a3 3 0 0 0 2.1 2.1c1.9.6 8.4.6 8.4.6s6.5 0 8.4-.6a3 3 0 0 0 2.1-2.1C23 15.9 23 12 23 12ZM9.8 15.6V8.4l6.2 3.6-6.2 3.6Z',
  x:
    'M18.2 2H21l-6.4 7.3L22.3 22h-6l-4.7-6.2L6.2 22H3.4l6.9-7.9L2 2h6.1l4.3 5.7L18.2 2Zm-1 18.3h1.6L7.9 3.6H6.2l11 16.7Z',
  instagram:
    'M12 2.2c3.2 0 3.6 0 4.9.1 1.2.1 1.8.3 2.2.4.6.2 1 .5 1.4.9.4.4.7.8.9 1.4.2.4.4 1 .4 2.2.1 1.3.1 1.7.1 4.9s0 3.6-.1 4.9c-.1 1.2-.3 1.8-.4 2.2-.2.6-.5 1-.9 1.4-.4.4-.8.7-1.4.9-.4.2-1 .4-2.2.4-1.3.1-1.7.1-4.9.1s-3.6 0-4.9-.1c-1.2-.1-1.8-.3-2.2-.4-.6-.2-1-.5-1.4-.9-.4-.4-.7-.8-.9-1.4-.2-.4-.4-1-.4-2.2C2.2 15.6 2.2 15.2 2.2 12s0-3.6.1-4.9c.1-1.2.3-1.8.4-2.2.2-.6.5-1 .9-1.4.4-.4.8-.7 1.4-.9.4-.2 1-.4 2.2-.4C8.4 2.2 8.8 2.2 12 2.2Zm0 3.2A6.6 6.6 0 1 0 18.6 12 6.6 6.6 0 0 0 12 5.4Zm0 10.9A4.3 4.3 0 1 1 16.3 12 4.3 4.3 0 0 1 12 16.3Zm6.9-11.1a1.5 1.5 0 1 1-1.5-1.5 1.5 1.5 0 0 1 1.5 1.5Z',
  discord:
    'M20.3 4.9A19.8 19.8 0 0 0 15.4 3.4l-.2.4a18.3 18.3 0 0 1 4.4 1.4 15.6 15.6 0 0 0-15.2 0A18.3 18.3 0 0 1 8.8 3.8l-.2-.4A19.8 19.8 0 0 0 3.7 4.9C.6 9.5-.3 14 .2 18.4a19.9 19.9 0 0 0 6 3.1l1.2-1.7a13 13 0 0 1-2-1l.5-.4a14.2 14.2 0 0 0 12.2 0l.5.4a13 13 0 0 1-2 1l1.2 1.7a19.9 19.9 0 0 0 6-3.1c.6-5.1-.8-9.6-3.5-13.5ZM8.3 15.6c-1.2 0-2.1-1.1-2.1-2.4s.9-2.4 2.1-2.4 2.2 1.1 2.2 2.4-1 2.4-2.2 2.4Zm7.4 0c-1.2 0-2.1-1.1-2.1-2.4s.9-2.4 2.1-2.4 2.2 1.1 2.2 2.4-1 2.4-2.2 2.4Z',
  facebook:
    'M22 12a10 10 0 1 0-11.6 9.9v-7H7.9V12h2.5V9.8c0-2.5 1.5-3.9 3.8-3.9 1.1 0 2.2.2 2.2.2v2.5h-1.3c-1.2 0-1.6.8-1.6 1.6V12h2.8l-.4 2.9h-2.4v7A10 10 0 0 0 22 12Z',
  website:
    'M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2Zm6.9 6h-2.9a15.6 15.6 0 0 0-1.3-3.6A8 8 0 0 1 18.9 8ZM12 4.1A14 14 0 0 1 13.8 8h-3.6A14 14 0 0 1 12 4.1ZM4.3 14a7.9 7.9 0 0 1 0-4h3.3a17.6 17.6 0 0 0 0 4Zm.8 2h2.9a15.6 15.6 0 0 0 1.3 3.6A8 8 0 0 1 5.1 16Zm2.9-8H5.1a8 8 0 0 1 4.2-3.6A15.6 15.6 0 0 0 8 8Zm4 11.9A14 14 0 0 1 10.2 16h3.6A14 14 0 0 1 12 19.9ZM14.2 14H9.8a15.8 15.8 0 0 1 0-4h4.4a15.8 15.8 0 0 1 0 4Zm.5 5.6a15.6 15.6 0 0 0 1.3-3.6h2.9a8 8 0 0 1-4.2 3.6Zm1.7-5.6a17.6 17.6 0 0 0 0-4h3.3a7.9 7.9 0 0 1 0 4Z',
}

const HOSTS: [RegExp, SocialPlatform][] = [
  [/(^|\.)(discord\.gg|discord\.com|discordapp\.com)$/, 'discord'],
  [/(^|\.)instagram\.com$/, 'instagram'],
  [/(^|\.)twitch\.tv$/, 'twitch'],
  [/(^|\.)(twitter\.com|x\.com)$/, 'x'],
  [/(^|\.)(youtube\.com|youtu\.be)$/, 'youtube'],
  [/(^|\.)facebook\.com$/, 'facebook'],
]

/** The platform a URL points at; anything unrecognised is a plain website. */
export function socialPlatformOf(href: string): SocialPlatform {
  try {
    const host = new URL(href).hostname.toLowerCase().replace(/^www\./, '')
    return HOSTS.find(([re]) => re.test(host))?.[1] ?? 'website'
  } catch {
    return 'website'
  }
}

/** At most one link per platform. */
export type SocialLinks = Partial<Record<SocialPlatform, string>>

export interface SocialLink {
  platform: SocialPlatform
  url: string
}

/** Example link per platform, inserted by the description toolbar. */
export const SOCIAL_PLACEHOLDER: Record<SocialPlatform, string> = {
  discord: 'discord.gg/your-server',
  twitch: 'twitch.tv/your-channel',
  x: 'x.com/your-handle',
  instagram: 'instagram.com/your-handle',
  youtube: 'youtube.com/@your-channel',
  facebook: 'facebook.com/your-page',
  website: 'your-site.com',
}

/** Untrusted platform → URL bag → known platforms with http(s) URLs only. */
export function parseSocialLinks(raw: unknown): SocialLinks {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {}
  const out: SocialLinks = {}
  for (const platform of SOCIAL_PLATFORMS) {
    const value = (raw as Record<string, unknown>)[platform]
    if (typeof value === 'string' && /^https?:\/\//i.test(value)) out[platform] = value
  }
  return out
}

/**
 * Merges link sources into one row of buttons, one per platform, in display
 * order. Earlier sources win: pass the most authoritative first.
 */
export function mergeSocialLinks(...sources: SocialLinks[]): SocialLink[] {
  return SOCIAL_PLATFORMS.flatMap((platform) => {
    const url = sources.find((s) => s[platform])?.[platform]
    return url ? [{ platform, url }] : []
  })
}
